/**
 * PWA Machine translations - Universal mapping of machine codenames to display names
 * This file serves as the single source of truth for machine name translations
 * across the entire application.
 */

export const MACHINE_TRANSLATIONS: Record<string, string> = {
  // Regular machines (ALFA)
  "LEG_EXTENSION": "LEG EXTENSION ALFA",
  "CHEST_PRESS": "CHEST PRESS ALFA",
  "SEATED_ROW": "SEATED ROW ALFA",
  "ERGOMETR": "ERGOMETR ALFA",
  "LEG_CURL": "LEG CURL ALFA",
  "ABDOMINAL_CRUNCH": "ABDOMINAL CRUNCH ALFA",
  "BACK_EXTENSION": "BACK EXTENSION ALFA",
  "CROSSWALKER": "CROSSWALKER ALFA",
  "ABDUCTOR_B1": "ABDUCTOR ALFA",
  "ABDUCTOR_B2": "ADDUCTOR ALFA",
  "LAT_PULL_DOWN": "LAT PULL DOWN ALFA",
  "TRICEPS": "TRICEPS ALFA",
  "LEG_PRESS": "LEG PRESS ALFA",
  "BUTTERFLY_REV": "BUTTERFLY REV ALFA",
  "BUTTERFLY": "BUTTERFLY ALFA",
  "BICEPS": "BICEPS ALFA",
  "LAT_PULL_DOWN_B": "LAT PULL DOWN B ALFA",
  "SHOULDER_PRESS_B": "SHOULDER PRESS B ALFA",
  "ROTATOR_LEFT_B": "ROTATOR LEFT B ALFA",
  "ROTATOR_RIGHT_B": "ROTATOR RIGHT B ALFA",
  "LEG_PRESS_45": "LEG PRESS 45 ALFA",
  "GLUTEUS_STAND_B": "GLUTEUS STAND B ALFA",

  // BASIC machines
  "BASIC_LEG_EXTENSION": "LEG EXTENSION",
  "BASIC_CHEST_PRESS": "CHEST PRESS",
  "BASIC_SEATED_ROW": "SEATED ROW",
  "BASIC_ERGOMETR": "ERGOMETR",
  "BASIC_LEG_CURL": "LEG CURL",
  "BASIC_ABDOMINAL_CRUNCH": "ABDOMINAL CRUNCH",
  "BASIC_BACK_EXTENSION": "BACK EXTENSION",
  "BASIC_CROSSWALKER": "CROSSWALKER",
  "BASIC_ABDUCTOR_B1": "ABDUCTOR",
  "BASIC_ABDUCTOR_B2": "ADDUCTOR",
  "BASIC_LAT_PULL_DOWN": "LAT PULL DOWN",
  "BASIC_TRICEPS": "TRICEPS",
  "BASIC_LEG_PRESS": "LEG PRESS",
  "BASIC_BUTTERFLY_REV": "BUTTERFLY REV",
  "BASIC_BUTTERFLY": "BUTTERFLY",
  "BASIC_BICEPS": "BICEPS",
  "BASIC_LAT_PULL_DOWN_B": "LAT PULL DOWN B",
  "BASIC_SHOULDER_PRESS_B": "SHOULDER PRESS B",
  "BASIC_ROTATOR_LEFT_B": "ROTATOR LEFT B",
  "BASIC_ROTATOR_RIGHT_B": "ROTATOR RIGHT B",
  "BASIC_LEG_PRESS_45": "LEG PRESS 45",
  "BASIC_GLUTEUS_STAND_B": "GLUTEUS STAND B",
  "BASIC_ROTATOR_LEFT_C": "ROTATOR LEFT C",
  "BASIC_ROTATOR_RIGHT_C": "ROTATOR RIGHT C",
  "BASIC_LEG_PRESS_VARIOS_B": "LEG PRESS VARIOS B",
} as const;

/**
 * Get the translated display name for a machine codename
 * @param machineCodename - The machine codename (e.g., "BASIC_LEG_EXTENSION")
 * @returns The translated display name, or the original codename if no translation exists
 */
export function getMachineDisplayName(machineCodename: string): string {
  return MACHINE_TRANSLATIONS[machineCodename] || machineCodename;
}

/**
 * Get all machine types formatted for dropdowns/selects
 * @returns Array of objects with value (codename) and label (display name) properties
 */
export function getMachineOptions(): Array<{ value: string; label: string }> {
  return Object.entries(MACHINE_TRANSLATIONS).map(([codename, displayName]) => ({
    value: codename,
    label: displayName,
  }));
}

/**
 * Check if a machine is a BASIC type
 * @param machineCodename - The machine codename
 * @returns True if the machine is a BASIC type
 */
export function isBasicMachine(machineCodename: string): boolean {
  return machineCodename.startsWith('BASIC_');
}

/**
 * Generate initials from machine display name for Avatar
 * @param displayName - The translated machine display name
 * @returns Initials string (2-3 characters)
 */
export function getMachineInitials(displayName: string): string {
  const words = displayName.trim().split(' ').filter(word => word.length > 0);
  
  if (words.length === 0) return 'MA'; // fallback
  
  if (words.length === 1) {
    // Single word: take first 2 characters
    return words[0].substring(0, 2).toUpperCase();
  } else if (words.length === 2) {
    // Two words: take first character of each
    return (words[0][0] + words[1][0]).toUpperCase();
  } else {
    // Three or more words: take first character of first 3 words, but skip common words
    const skipWords = ['ALFA', 'BASIC', 'B']; // Words to skip for initials
    const meaningfulWords = words.filter(word => !skipWords.includes(word.toUpperCase()));
    
    if (meaningfulWords.length >= 2) {
      return (meaningfulWords[0][0] + meaningfulWords[1][0]).toUpperCase();
    } else if (meaningfulWords.length === 1) {
      return meaningfulWords[0].substring(0, 2).toUpperCase();
    } else {
      // Fallback to first two words if all are "skip words"
      return (words[0][0] + words[1][0]).toUpperCase();
    }
  }
}
